import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight, Calendar, BookOpen, MessageCircle } from 'lucide-react';
import { useLatestNews, useLatestBlog, useLocalize } from '@/hooks/useLandingContent';
import { optimizedImageUrl } from '@/lib/imageOptimizer';

const FORUM_CATEGORIES = [
  { name: 'Buying Property', topics: 11, color: 'bg-amber-500' },
  { name: 'Selling House or Plot', topics: 11, color: 'bg-sky-500' },
  { name: 'Renting Property', topics: 11, color: 'bg-fuchsia-500' },
  { name: 'Best Areas to Invest', topics: 11, color: 'bg-emerald-500' },
];

export function CommunitySection() {
  const navigate = useNavigate();
  const localize = useLocalize();
  const { data: news } = useLatestNews(4);
  const { data: blog } = useLatestBlog(3);

  const fmtDate = (s?: string | null) =>
    s ? new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '';

  return (
    <section className="bg-muted/30 py-12">
      <div className="container">
        <h2 className="font-display text-2xl md:text-3xl font-bold mb-8">Our Community</h2>

        {/* Row 1: News + Blogs */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-10">
          {/* News column */}
          <div className="lg:col-span-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg">News</h3>
              <Link to="/news" className="text-xs font-semibold text-primary hover:underline flex items-center gap-1">
                View All <ChevronRight className="h-3.5 w-3.5" />
              </Link>
            </div>
            <ul className="space-y-4">
              {(news || []).map((n: any) => (
                <li
                  key={n.id}
                  className="flex gap-3 cursor-pointer group"
                  onClick={() => navigate('/news')}
                >
                  <div className="w-16 h-16 rounded-md overflow-hidden bg-muted shrink-0">
                    {n.cover_url && (
                      <img
                        src={optimizedImageUrl(n.cover_url, { width: 128, height: 128, quality: 70 })}
                        alt=""
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        loading="lazy"
                      />
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold leading-snug line-clamp-2 group-hover:text-primary">
                      {localize(n.title)}
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1">
                      <Calendar className="h-3 w-3" /> {fmtDate(n.published_at)}
                    </div>
                  </div>
                </li>
              ))}
              {!news?.length && <li className="text-sm text-muted-foreground">No news yet.</li>}
            </ul>
          </div>

          {/* Blogs column */}
          <div className="lg:col-span-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg">Blogs</h3>
              <Link to="/blogs" className="text-xs font-semibold text-primary hover:underline flex items-center gap-1">
                View All <ChevronRight className="h-3.5 w-3.5" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {(blog || []).map((p: any) => (
                <article
                  key={p.id}
                  className="cursor-pointer group"
                  onClick={() => navigate('/blogs')}
                >
                  <div className="aspect-[16/10] rounded-xl overflow-hidden bg-muted mb-3">
                    {p.cover_url && (
                      <img
                        src={optimizedImageUrl(p.cover_url, { width: 600, height: 380, quality: 75 })}
                        alt=""
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    )}
                  </div>
                  <div className="text-[11px] text-muted-foreground mb-1.5 flex items-center gap-2">
                    <span>By Admin</span>
                    <span className="text-border">·</span>
                    <Calendar className="h-3 w-3" /> {fmtDate(p.published_at)}
                  </div>
                  <h4 className="text-sm font-bold leading-snug line-clamp-2 group-hover:text-primary">
                    {localize(p.title)}
                  </h4>
                  <button className="text-xs text-primary font-semibold mt-2 inline-flex items-center gap-1 hover:underline">
                    Read more <ChevronRight className="h-3 w-3" />
                  </button>
                </article>
              ))}
              {!blog?.length && <div className="text-sm text-muted-foreground">No blog posts yet.</div>}
            </div>
          </div>
        </div>

        {/* Row 2: Forums */}
        <div className="mb-10">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-lg">Forums</h3>
            <Link to="/forum" className="text-xs font-semibold text-primary hover:underline flex items-center gap-1">
              View All <ChevronRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {FORUM_CATEGORIES.map((c) => (
              <button
                key={c.name}
                onClick={() => navigate('/forum')}
                className="rounded-xl border bg-card p-4 flex items-center gap-3 text-left hover:shadow-md hover:border-primary/40 transition"
              >
                <span className={`h-9 w-9 rounded-full ${c.color} flex items-center justify-center text-white shrink-0`}>
                  <MessageCircle className="h-4 w-4" />
                </span>
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{c.name}</div>
                  <div className="text-[11px] text-muted-foreground">{c.topics} Topics</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Row 3: Events + Guides */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="rounded-xl bg-card border p-5">
            <Calendar className="h-6 w-6 text-primary mb-2" />
            <div className="font-semibold text-sm">Property Events</div>
            <div className="text-xs text-muted-foreground mt-1">Open houses, expos & developer launches.</div>
          </div>
          <button
            onClick={() => navigate('/guides/buyer')}
            className="rounded-xl bg-card border p-5 text-left hover:shadow-md transition"
          >
            <BookOpen className="h-6 w-6 text-primary mb-2" />
            <div className="font-semibold text-sm">Buyer Guide</div>
            <div className="text-xs text-muted-foreground mt-1">Step-by-step help finding and buying your home.</div>
          </button>
          <button
            onClick={() => navigate('/guides/seller')}
            className="rounded-xl bg-card border p-5 text-left hover:shadow-md transition"
          >
            <BookOpen className="h-6 w-6 text-primary mb-2" />
            <div className="font-semibold text-sm">Seller Guide</div>
            <div className="text-xs text-muted-foreground mt-1">List, market, and close on your property.</div>
          </button>
        </div>
      </div>
    </section>
  );
}
